import type { ReactNode } from 'react';
import Link from 'next/link';
import clsx from 'clsx';

import { Flex, Stack, Text, withErrorBoundary } from '@highlight-cp/core-ui';

import { HighlightLogo } from '../highlight-logo';
import { LOGO_VARIANT } from '../highlight-logo/constants';
import { ColorSchemeToggle, TopNavAvatarMenu, TopNavLink } from './components';
import type { TopNavViewer } from './components/top-nav-avatar-menu/top-nav-avatar-menu';

export type { TopNavViewer };
import { TEST_ID } from './constants/testIds';
import classes from './top-nav.module.css';

export interface TopNavProps {
  isAdmin?: boolean;
  children?: ReactNode;
  className?: string;
  menu: {
    label: string;
    href: string;
    isActive?: boolean;
    testId?: string;
    target?: string;
    /** Rendered before the label, inside the link. A currentColor mark follows the link text. */
    icon?: ReactNode;
    /** Rendered after the label, inside the same link. */
    badge?: ReactNode;
    /**
     * Menu content that opens on hover, under the link. Pass `Menu.Item`s, labels and dividers.
     * It mounts only while open.
     */
    dropdown?: ReactNode;
  }[];
  /**
   * The avatar menu as one cohesive optional feature: when present, the
   * viewer is required — the presenter does no session fetching of its own.
   */
  avatarMenu?: {
    viewer: TopNavViewer;
    onLogout: () => void;
    items?: { label: string; href: string }[];
  };
  /** Show the light/dark color-scheme switch in the nav, between children and the avatar menu. */
  showColorSchemeToggle?: boolean;
  light?: boolean;
}

const TopNav = ({
  children,
  className,
  menu,
  isAdmin = false,
  avatarMenu,
  showColorSchemeToggle = false,
  light = false,
}: TopNavProps) => (
  <nav
    className={clsx(
      classes.root,
      isAdmin && classes.adminBorder,
      light && classes.light,
      className,
    )}
  >
    <Flex align="center" gap={24}>
      <Stack gap={0} align="center">
        <Link
          href={isAdmin ? '/project' : '/home'}
          data-testid={TEST_ID.TOP_NAV_HOME_BUTTON}
          className={classes.logoContainer}
        >
          <HighlightLogo
            height={30}
            width="auto"
            variant={isAdmin ? LOGO_VARIANT.White : LOGO_VARIANT.Color}
          />
        </Link>
        {isAdmin && (
          <Text size="sm" fw={600} c="white">
            ADMIN APP
          </Text>
        )}
      </Stack>

      <Flex gap="xs" align="center">
        {menu.map((item) => (
          <TopNavLink key={item.label} {...item} />
        ))}
      </Flex>
    </Flex>
    {/* Roomier than the nav links on the left: these are unrelated controls, not a group. */}
    <Flex gap="md" align="center">
      {children}
      {showColorSchemeToggle && <ColorSchemeToggle />}
      {avatarMenu && (
        <TopNavAvatarMenu
          viewer={avatarMenu.viewer}
          onLogout={avatarMenu.onLogout}
          menuItems={avatarMenu.items}
        />
      )}
    </Flex>
  </nav>
);

export default withErrorBoundary(TopNav, { componentName: 'top nav' });
